import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/src/app/context/useAuth';
import { Container, ErrorMessage, Loading } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { type ExamAssignmentDTO } from './models/examAttemptDTOs';
import AdminViewStudentSearch from './components/AdminViewStudentSearch';
import ExamsTable from './components/ExamsTable';

const ViewExamsPage: React.FC = () => {
    document.title = 'Your Assessments';

    const navigate = useNavigate();

    const { user } = useAuth();

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    const [adminViewStudentId, setAdminviewStudentId] = useState('');
    const [data, setData] = useState<ExamAssignmentDTO[]>([]);

    const refreshTableData = useCallback(
        async (studentId?: string) => {
            setError('');
            setLoaded(false);

            if (!studentId) {
                studentId = user?.id?.toString();
            }

            let requestUrl = `assessments/examAssignments/student`;
            if (studentId) {
                requestUrl += `/${studentId}`;
            }

            try {
                const res = await apiRequest<ExamAssignmentDTO[]>('GET', requestUrl);
                setData(res.data);
            } catch {
                setError('Failed to fetch assessment data');
            } finally {
                setLoaded(true);
            }
        },
        [user?.id]
    );

    useEffect(() => {
        (async function init() {
            await refreshTableData();
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tableActionOnClick = useCallback(
        async (createNewAttempt: boolean, actionId: number) => {
            // actionId is examAssignmentId when creating an attempt, studentExamAttemptId otherwise
            setError('');
            setLoaded(false);

            let wasSuccessful = false;
            if (createNewAttempt) {
                try {
                    const res = await apiRequest<{ id: number }>(
                        'POST',
                        `assessments/studentExamAttempts`,
                        {
                            examAssignmentId: actionId,
                        }
                    );

                    actionId = res.data.id;
                    wasSuccessful = true;
                } catch {
                    setError('Failed to create new assessment attempt.');
                } finally {
                    setLoaded(true);
                }

                if (!wasSuccessful) {
                    return;
                }
            }

            setLoaded(true);
            navigate(`/studentExamView/attempt/${actionId}/start`);
        },
        [navigate]
    );

    return (
        <Container>
            <h1 className="page-header">Your Assessments</h1>
            <br />

            {user?.role === 'Super Admin' && (
                <AdminViewStudentSearch
                    adminViewStudentId={adminViewStudentId}
                    setAdminviewStudentId={setAdminviewStudentId}
                    refreshTableData={refreshTableData}
                />
            )}
            <br />
            <Loading visible={!loaded} message="Loading assessments..." />
            <ErrorMessage error={error} style={{ textAlign: 'center' }} />

            {loaded && <ExamsTable data={data} tableActionOnClick={tableActionOnClick} />}
        </Container>
    );
};

export default ViewExamsPage;
